/**
 * Programs — what each thing in the hub is for, and when to use which.
 *
 * ⚠️ THE PAGE EXISTS BECAUSE TWO OF THESE LOOK IDENTICAL FROM OUTSIDE. COMM-PASS
 * and the Submission Portal both start with "I have a commercial risk", and an
 * agent choosing wrongly either waits for help that isn't coming or hands away
 * an account they meant to keep. Josh, 2026-08-31: "Submissions is wholly on
 * their own. AAS will have no part in that process. COMM-PASS is the way to go
 * when they need their hands held."
 *
 * ⚠️ ONLY WHAT THE AGENCY ACTUALLY HAS. Describing a programme somebody cannot
 * open is an advertisement, and this page is meant to answer a question, not
 * create one.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { HUB_MODULES } from '@commpass/shared';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';


export function Programs() {
  const navigate = useNavigate();
  const { email, isStaff } = useAuth();
  const [modules, setModules] = useState<string[] | null>(null);

  useEffect(() => {
    api
      .hubMe()
      .then((r) => setModules(r.modules))
      .catch(() => setModules([]));
  }, [email]);

  /**
   * ⚠️ NO FILTER ON "DOES IT HAVE A DESCRIPTION" ANY MORE, BECAUSE IT CANNOT NOT
   * HAVE ONE. This page used to drop modules with no entry in a local lookup,
   * silently — which is how Loss Runs and Report a Loss were both missing here
   * until somebody noticed. The description now lives on the module itself and
   * is a required field, so a new module fails to compile rather than quietly
   * failing to appear.
   */
  const mine = HUB_MODULES.filter((m) => (modules ?? []).includes(m.id));

  return (
    <div className="wrap">
      <PageHero
        title="Your programmes"
        sub={`What each one is for, and which to reach for.${isStaff ? ' Staff see every programme.' : ''}`}
      />

      {modules === null ? (
        <div className="empty">
          <p>Loading…</p>
        </div>
      ) : !mine.length ? (
        <div className="empty">
          <h3>Nothing here yet</h3>
          <p>Your agency has not been given any of the hub programmes. Speak to your Agents Alliance contact.</p>
        </div>
      ) : (
        <div className="prog-list">
          {mine.map((m) => {
            const x = m.guide;
            return (
              <section className="prog-card" key={m.id}>
                <div className="prog-head">
                  {/* The module draws its own icon — the same glyph the rail and
                      the hub tiles use, so a card is recognisable before its
                      title is read. */}
                  <span className="prog-ic" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d={m.icon} />
                    </svg>
                  </span>
                  <h3>{m.name}</h3>
                </div>
                <span className="prog-who">{x.who}</span>
                <p className="prog-does">{x.does}</p>
                <div className="prog-when">
                  <div>
                    <b>Use it when</b>
                    <span>{x.useWhen}</span>
                  </div>
                  {/* ⚠️ KEPT, THOUGH THE MOCK DROPS IT. Knowing when NOT to open
                      something is the half that prevents the wrong choice, and
                      preventing the wrong choice between COMM-PASS and the
                      Submission Portal is the entire reason this page exists. */}
                  <div>
                    <b>Not for</b>
                    <span>{x.notFor}</span>
                  </div>
                </div>
                <button
                  className="btn btn-ghost"
                  onClick={() => (m.external ? window.open(m.href, '_blank', 'noopener,noreferrer') : navigate(m.href))}
                >
                  Open {m.name} →
                </button>
              </section>
            );
          })}
        </div>
      )}

      <p className="supp-note" style={{ maxWidth: 720, marginTop: 18 }}>
        Still not sure which to use? Email{' '}
        <a href="mailto:CLHelp@agentsalliance.biz">CLHelp@agentsalliance.biz</a> and ask — that is the
        commercial lines desk, and a two-line answer from them beats guessing.
      </p>
    </div>
  );
}
